import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaCopy, FaRedo } from "react-icons/fa";

export default function LoremIpsumGenerator() {
  const [paragraphCount, setParagraphCount] = useState(1);
  const [avgWordsPerSentence, setAvgWordsPerSentence] = useState(8);
  const [avgSentencesPerParagraph, setAvgSentencesPerParagraph] = useState(8);
  const [generatedText, setGeneratedText] = useState("");
  const { darkMode } = useTheme();

  const loremWords = [
    "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
    "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
    "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
    "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
    "consequat", "duis", "aute", "irure", "in", "reprehenderit", "voluptate", "velit",
    "esse", "cillum", "eu", "fugiat", "nulla", "pariatur", "excepteur", "sint",
    "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
    "deserunt", "mollit", "anim", "id", "est", "laborum"
  ];

  const generateLoremIpsum = () => {
    let result = [];
    
    for (let p = 0; p < paragraphCount; p++) {
      let paragraph = [];
      
      for (let s = 0; s < avgSentencesPerParagraph; s++) {
        let sentence = [];
        const sentenceLength = avgWordsPerSentence + Math.floor(Math.random() * 4) - 2; // Add some variation
        
        for (let w = 0; w < sentenceLength; w++) {
          const randomWord = loremWords[Math.floor(Math.random() * loremWords.length)];
          sentence.push(w === 0 ? randomWord.charAt(0).toUpperCase() + randomWord.slice(1) : randomWord);
        }
        
        paragraph.push(sentence.join(" ") + ".");
      }
      
      result.push(paragraph.join(" "));
    }
    
    setGeneratedText(result.join("\n\n"));
  };

  const copyToClipboard = () => {
    if (!generatedText) return;
    navigator.clipboard.writeText(generatedText);
  };

  const resetGenerator = () => {
    setParagraphCount(1);
    setAvgWordsPerSentence(8);
    setAvgSentencesPerParagraph(8);
    setGeneratedText("");
  };

  // Generate on initial render and when settings change
  React.useEffect(() => {
    generateLoremIpsum();
  }, [paragraphCount, avgWordsPerSentence, avgSentencesPerParagraph]);

  return (
    <>
      <HeaderSection toolId="loremIpsumGenerator" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">
              Lorem Ipsum Generator
            </h1>
            <p className="opacity-90">
              Generate placeholder text for your designs and mockups
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Paragraph Count
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                  value={paragraphCount}
                  onChange={(e) => setParagraphCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Avg. Words Per Sentence
                </label>
                <input
                  type="number"
                  min="3"
                  max="20"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                  value={avgWordsPerSentence}
                  onChange={(e) => setAvgWordsPerSentence(Math.max(3, Math.min(20, parseInt(e.target.value) || 8)))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Avg. Sentences Per Paragraph
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                  value={avgSentencesPerParagraph}
                  onChange={(e) => setAvgSentencesPerParagraph(Math.max(1, Math.min(20, parseInt(e.target.value) || 8)))}
                />
              </div>
            </div>

            {/* Generated Text */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Generated Lorem Ipsum Text
              </label>
              <div className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 h-64 overflow-y-auto whitespace-pre-line">
                {generatedText || "Your generated text will appear here..."}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetGenerator}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                <FaRedo /> Reset
              </button>
              <button
                onClick={copyToClipboard}
                disabled={!generatedText}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition ${generatedText ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'}`}
              >
                <FaCopy /> Copy Text
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Generate placeholder text for your designs and mockups</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="loremIpsumGenerator" />
    </>
  );
}